// ─── I pezzi che la prenotazione NUOVA e quella ESISTENTE hanno in comune ────
//
// 🔄⭐⭐ **NASCE IL 17/09/2026, E NASCE DA UNA RICHIESTA SUA:**
// 🗣️ *«se all'inizio se ne aprono due per poter poi cominciare a fare una
//   prenotazione, si può massimizzare in un'apertura, e dentro quella scheda si
//   fa tutto»*.
//
// ⚖️ Il 16/09 le due schede erano diventate **un pannello solo**, ma dentro
// quel pannello restavano due moduli diversi: prima si creava la prenotazione
// (tipo, durata, titolo), poi compariva un'altra faccia dove si mettevano le
// persone. Il posto sullo schermo era uno; le schermate da attraversare, due.
// ⇒ Perché ne resti **una**, le due facce devono avere gli **stessi pezzi
//   nello stesso ordine**: allora premere «Crea» non cambia schermata, accende
//   quella che si sta già guardando.
//
// 📌 *Due moduli che si somigliano sono due moduli. Uno solo, riempito due
//    volte, è una scheda.*

import { useEffect, useState } from 'react';
import {
  CADENZA,
  GIORNI_SETTIMANA,
  PASSO_MASSIMO,
  ORIZZONTE_GIORNI,
  SIGLE_SETTIMANA,
  DURATA_DEL_TIPO,
  ETICHETTE_LIVELLO,
  LIVELLI_CORSO,
  TIPI_BOOKING,
  VOLTE_MASSIME,
  comeSiRipete,
  etichettaCadenza,
  giorniDellaSerie,
  giorniScelti,
  indiceDelGiorno,
  maestroGiaAltrove,
  socioGiaAltrove,
  minutiDaOra,
  oraPiuMinuti,
  passoDi,
  piuGiorni,
  ALLIEVO_DA_SEGNARE,
  siPrezzaColListinoLezioni,
  vuoleMaestro,
  type Cadenza,
  type Prenotazione,
  type LivelloCorso,
  type Maestro,
  type PersonaTrovata,
  type RegolaRipetizione,
  type TipoBooking,
} from '@pmo/shared';
import { CampoData } from '../../components/CampoData.js';
import { chiama } from '../../lib/api.js';
import { giornoSenzaAnno } from '../../lib/time.js';
import { etichettaTipo } from './Griglia.js';

/**
 * Le durate che si scelgono da un menu, chieste da lui: 30 · 60 · 90 · 120 · 180.
 *
 * ⚠️ È un elenco di SCORCIATOIE, non il confine di ciò che il server accetta: una
 * durata fuori elenco resta legittima (la maniglia del calendario ne fa di
 * qualunque multiplo di un quarto d'ora) e infatti, se arriva, il menu se la
 * tiene invece di arrotondarla alla voce più vicina.
 * 📌 *Un menu che scarta il valore che ha ricevuto non è una comodità: è una
 *    modifica fatta senza chiederla.*
 */
export const DURATE = [30, 60, 90, 120, 180];

/**
 * `90` → `1 ora e 30`. Le ore si contano in ore, non in minuti: «180 minuti» si
 * legge due volte, «3 ore» una sola — e chi prenota ragiona in ore.
 */
export function etichettaDurata(minuti: number): string {
  if (minuti < 60) return `${minuti} minuti`;
  const ore = Math.floor(minuti / 60);
  const resto = minuti % 60;
  const parteOre = ore === 1 ? '1 ora' : `${ore} ore`;
  return resto === 0 ? parteOre : `${parteOre} e ${resto}`;
}

/**
 * ⭐⭐ **CHE COSA UNA PRENOTAZIONE NON PUÒ NON AVERE**, in un posto solo.
 *
 * 🗣️ Regole sue del 17/09/2026, date in tre messaggi di fila:
 *   *«se prenoto una partita, ci deve essere obbligatoriamente un nome e
 *   cognome. Se invece manutenzione, non ci deve essere obbligatoriamente un
 *   nome e cognome»* · *«in una prenotazione di una lezione e di un corso è
 *   obbligatorio mettere il maestro»* · e, alla domanda su torneo e corso,
 *   **«solo la partita, gli altri liberi»** per il nome.
 *
 * | tipo | senza questo non si salva |
 * |---|---|
 * | partita | almeno una persona in campo |
 * | lezione · corso | il maestro |
 * | torneo · manutenzione | niente |
 *
 * ⚖️ **Sta qui e non in due copie** perché le due facce della scheda — quella
 * nuova e quella che c'è — devono spegnere «Crea» e «Salva» per lo **stesso**
 * motivo e con le **stesse** parole. Due elenchi di regole scritti a mano nei
 * due posti divergono al primo tipo che si aggiunge.
 * 🔒 E non è l'unica copia che conta: la stessa regola sta anche sul **server**
 *   (`regole.ts`), che è quello che decide davvero. Questa qui serve a dirlo
 *   **prima** di premere, invece di farlo scoprire da un rifiuto.
 *
 * ⇒ Rende la frase da mostrare, o `null` se non manca niente.
 */
export function cosaManca(tipo: TipoBooking, quantiInCampo: number, maestro: string): string | null {
  if (tipo === 'partita' && quantiInCampo === 0) {
    return 'Una partita vuole almeno una persona in campo: mettila qui sopra, e poi si salva.';
  }
  if (vuoleMaestro(tipo) && maestro === '') {
    /* ⚠️ «Qui sopra» e non «qui sotto»: dal riordino del 17/09 il maestro sta in
       cima alla scheda, e questa frase si legge nel piede. Una parola sbagliata
       manda a cercare un campo dalla parte opposta. */
    const chi =
      tipo === 'lezione'
        ? 'Una lezione'
        : tipo === 'allenamento'
          ? 'Un allenamento'
          : tipo === 'tattica'
            ? 'Una partita tattica'
            : 'Un corso';
    return `${chi} vuole un maestro: sceglilo qui sopra, e poi si salva.`;
  }
  return null;
}

/**
 * Quello che si scrive nella parte «Dettagli», identico nelle due facce.
 *
 * 🔄⭐⭐ **IL «TITOLO» NON C'È PIÙ, dal 17/09/2026.**
 * 🗣️ Sue parole: *«nella scheda c'è un campo titolo, a me non serve. Io vorrei
 *   che il titolo di una prenotazione fosse la tipologia di prenotazione,
 *   quindi: partita, lezione, corso, manutenzione»*.
 * ⇒ Una prenotazione non si intitola: **si dice cos'è**. Il nome sul blocco del
 *   calendario e in testa alla scheda è ora `etichettaTipo(type)`, e non c'è
 *   niente da battere a mano.
 * ⚖️ Non è un campo nascosto: è un campo **tolto**. Tenere una casella che
 *   scrive un nome che nessuno mostra sarebbe peggio di non averla — si
 *   scriverebbe qualcosa credendo che si veda.
 * 🩹 Chi ha bisogno di scrivere due parole su una partita ha le **Note**, che
 *   restano: quelle non fingono di essere il nome della cosa.
 */
export interface ValoriDettagli {
  note: string;
  tipo: TipoBooking;
  /** `''` = nessun maestro. Un `<select>` non sa dire `null`. */
  maestro: string;
  durata: number;
}

/**
 * ⭐⭐ **LO STESSO MODULO PER LA PRENOTAZIONE NUOVA E PER QUELLA CHE C'È.**
 *
 * È **senza memoria propria**, di proposito: lo stato sta in chi lo chiama,
 * perché il tipo non serve solo qui — decide quante persone stanno in campo e
 * se il maestro è obbligatorio, cioè cose che si disegnano **fuori** da questo
 * modulo. ⚖️ Un componente che si tenesse dentro il tipo costringerebbe a
 * tenerne una seconda copia di sopra, e due copie della stessa scelta divergono
 * sempre: è la stessa ragione per cui le prenotazioni stanno in un posto solo.
 *
 * 🩹 **`durata` c'è anche sulla scheda esistente, e prima non c'era**: si poteva
 * cambiare solo trascinando la maniglia sul calendario. Toglierla dopo «Crea»
 * avrebbe fatto sparire un campo appena usato — e un campo che sparisce è il
 * modo più diretto per far sembrare due schede quella che è una sola.
 */
export function ModuloDettagli({
  idModulo,
  v,
  cambia,
  maestri,
  maestroDaTenere = null,
  invia,
  livello,
}: {
  /** L'identificativo del modulo: il bottone sta nel piede e lo raggiunge con `form=`. */
  idModulo: string;
  v: ValoriDettagli;
  cambia(p: Partial<ValoriDettagli>): void;
  maestri: Maestro[];
  /** ⚠️ Un maestro non più attivo resta nell'elenco finché è lui quello di questa partita. */
  maestroDaTenere?: string | null;
  invia(): void;
  /**
   * 🆕 19c: il livello del corso, solo nella prenotazione nuova — dice quale
   * quota del «Listino prezzi lezioni» paga ogni iscritto. La scheda di un
   * corso che c'è già non lo passa: il livello nasce col corso.
   */
  livello?: { valore: LivelloCorso; cambia(l: LivelloCorso): void };
}): React.JSX.Element {
  /*
   * ⏱️ 🆕 19c: **lezione, allenamento e corso hanno la durata del loro tipo** —
   * 1 ora, 1 ora e mezza, 1 ora (⇒ `DURATA_DEL_TIPO`). Si legge, non si sceglie.
   * ⚠️ Solo se la prenotazione HA quella durata: una lezione di prima da 90
   *    minuti mostra ancora il menu, o la scheda direbbe «1 ora» di una cosa
   *    che dura un'ora e mezza.
   */
  const durataDelTipo = siPrezzaColListinoLezioni(v.tipo) ? DURATA_DEL_TIPO[v.tipo] : null;
  const durataFissa = durataDelTipo !== null && v.durata === durataDelTipo;
  return (
    <form
      id={idModulo}
      className="dettagli"
      onSubmit={(e) => {
        e.preventDefault();
        invia();
      }}
    >
      {/* ⚖️ Due scelte corte su una riga: in un pannello da 33rem ci stanno, e
          tenerle una sotto l'altra costava una riga per niente. */}
      <div className="due-in-fila">
        <label className="campo">
          <span>Tipo</span>
          {/* 🚨 CAMBIANDO TIPO, UN MAESTRO CHE NON C'ENTRA PIÙ SE NE VA — sua,
              17/09/2026: *«se per sbaglio è inserito un maestro non deve
              apparire»*. Nasconderlo soltanto avrebbe lasciato in colonna un
              dato che la scheda non mostra più: invisibile, e ancora lì.
              📌 *Un campo che smette di avere senso non si nasconde: si
                 svuota, o resta a dire il falso a chi lo legge da un'altra
                 parte.* */}
          <select
            value={v.tipo}
            onChange={(e) => {
              const t = e.target.value as TipoBooking;
              // 🆕 19c: cambiando tipo, la durata diventa quella del tipo.
              const durata = siPrezzaColListinoLezioni(t) ? { durata: DURATA_DEL_TIPO[t] } : {};
              cambia(vuoleMaestro(t) ? { tipo: t, ...durata } : { tipo: t, maestro: '', ...durata });
            }}
          >
            {TIPI_BOOKING.map((t) => (
              <option key={t} value={t}>
                {etichettaTipo(t)}
              </option>
            ))}
          </select>
        </label>
        {durataFissa ? (
          <div className="campo">
            <span>Durata</span>
            <span className="durata-fissa">
              {v.durata === 90 ? '1 ora e mezza' : etichettaDurata(v.durata)} — la dice il tipo
            </span>
          </div>
        ) : (
          <label className="campo">
            <span>Durata</span>
            <select value={v.durata} onChange={(e) => cambia({ durata: Number(e.target.value) })}>
              {[...new Set([...DURATE, v.durata])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <option key={m} value={m}>
                    {etichettaDurata(m)}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      {/*
        ⚖️ **Il maestro si vede SOLO dove c'entra qualcosa** (⇒ `VUOLE_MAESTRO`).
        Su una partita o una manutenzione un menu fermo su «— nessuno —» è una
        domanda che non va fatta; su una lezione o un corso è obbligatorio
        (⇒ `cosaManca`).
        🔄 **E si nasconde ANCHE se uno c'è già, dal 17/09/2026.** Qui c'era
           `|| v.maestro !== ''`, scritto per non lasciare in piedi una scelta
           impossibile da togliere: ⇒ adesso non serve più, perché cambiando
           tipo il maestro **si toglie da sé** (vedi il menu qui sopra). Quella
           riga curava il sintomo; questa cura la causa.
      */}
      {vuoleMaestro(v.tipo) && (
        <div className={livello && v.tipo === 'corso' ? 'due-in-fila' : undefined}>
          <label className="campo">
            <span>Maestro</span>
            <select value={v.maestro} onChange={(e) => cambia({ maestro: e.target.value })} required>
              {/* 🔄 Dal 17/09/2026 anche il CORSO lo pretende: *«in una prenotazione
                  di una lezione e di un corso è obbligatorio mettere il maestro»*.
                  Qui era scritto solo `lezione`. */}
              <option value="">— scegli —</option>
              {maestri
                .filter((m) => m.active || m.id === maestroDaTenere)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
            </select>
          </label>
          {livello && v.tipo === 'corso' && (
            <label className="campo">
              <span>Livello</span>
              <select value={livello.valore} onChange={(e) => livello.cambia(e.target.value as LivelloCorso)}>
                {LIVELLI_CORSO.map((l) => (
                  <option key={l} value={l}>
                    {ETICHETTE_LIVELLO[l]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </form>
  );
}

/**
 * ⭐⭐ **LE NOTE STANNO IN FONDO, LONTANE DAL LORO MODULO — e ci appartengono
 * lo stesso.**
 *
 * 🗣️ Riordino chiesto da lui il 17/09/2026, in tre messaggi: *«quando si apre la
 *   scheda, la prima cosa che si deve vedere è la tipologia di prenotazione. E
 *   poi sotto il giocatore da inserire»* · *«di default lascia partita e la
 *   durata a fianco di default 90 minuti»* · *«logicamente poco sotto alla
 *   tipologia metti il maestro e poi sotto il settore per inserire i
 *   giocatori»*.
 * ⚖️ Ed è giusto anche per una ragione che lui non ha dovuto dire: **il tipo
 *   decide cosa è obbligatorio** — una partita vuole una persona, una lezione
 *   vuole il maestro — e decide quanti ci stanno in campo. Sceglierlo per ultimo
 *   vuol dire riempire un modulo e poi scoprirne le regole.
 * ⇒ Fra «che cos'è» e «chi c'è» non ci può stare una casella di testo libero: le
 *   note sono la coda della scheda, non un ostacolo in mezzo.
 *
 * 🔑 A tenerle attaccate al modulo che sta in cima è `form={idModulo}`: un campo
 * appartiene al suo modulo per **attributo**, non per posizione nella pagina. È
 * lo stesso meccanismo con cui «Salva» vive nel piede che non scorre.
 */
export function CampoNote({
  idModulo,
  v,
  cambia,
}: {
  idModulo: string;
  v: ValoriDettagli;
  cambia(p: Partial<ValoriDettagli>): void;
}): React.JSX.Element {
  return (
    <label className="campo">
      {/* ⚖️ **Il titolo della sezione dice già «Note».** Scritto due volte, uno
          sotto l'altro, sembrava un errore di disegno — ed era l'unica etichetta
          della scheda che ripeteva la riga sopra. Resta nel documento per chi
          legge a voce: sparisce il modo di mostrarla, non l'informazione. */}
      <span className="solo-a-voce">Note</span>
      <textarea
        form={idModulo}
        value={v.note}
        onChange={(e) => cambia({ note: e.target.value })}
        rows={2}
        maxLength={2000}
      />
    </label>
  );
}

/** L'importo si scrive in euro e si manda in centesimi: mai un decimale sul filo. */
export function ImportoModificabile({
  cents,
  salva,
}: {
  cents: number;
  salva(cents: number): void;
}): React.JSX.Element {
  /*
   * ⚠️ **CON LA VIRGOLA**, e fino al 17/09/2026 qui c'era il punto: in tutta
   * l'app un importo si scrive `20,00 €` — questa casella era l'unica a dire
   * `20.00`, accanto a un «pagato» che diceva `5,00 €`. Due modi di scrivere
   * gli euro nella stessa riga fanno dubitare che siano la stessa unità.
   * ⚖️ In lettura cambia solo la virgola: `parseCents` accetta già tutt'e due,
   *   quindi chi batte col punto continua a essere capito.
   */
  const scritto = (c: number): string => (c / 100).toFixed(2).replace('.', ',');
  const [testo, setTesto] = useState(scritto(cents));
  useEffect(() => setTesto(scritto(cents)), [cents]);
  return (
    <input
      className="importo"
      inputMode="decimal"
      value={testo}
      onChange={(e) => setTesto(e.target.value)}
      onBlur={() => {
        const n = Number(testo.replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) {
          setTesto(scritto(cents));
          return;
        }
        const nuovo = Math.round(n * 100);
        if (nuovo !== cents) salva(nuovo);
      }}
      aria-label="importo a carico in euro"
    />
  );
}

/**
 * 👥 19/10/2026 (mockup «Lezione doppia», approvato: *«sì, e vai»*): il posto di una lezione che resta libero
 * per l'amico invitato dal bot. Nel calendario si chiama così; dentro resta «Allievo da segnare», la parola
 * con cui bot e gestionale lo riconoscono (⇒ `entraInCampo`, che lo dà al primo socio che entra).
 */
export const POSTO_PER_UN_AMICO = 'Posto per un amico';
export const ePostoPerUnAmico = (g: { memberId: string | null; displayName?: string | null }): boolean =>
  g.memberId === null && g.displayName === ALLIEVO_DA_SEGNARE;

/**
 * Chi entra in campo: si cerca fra i soci, o si scrive il nome di un ospite.
 *
 * ⭐ **Rende anche il NOME, non solo l'identificativo**, e serve alla scheda
 * nuova: là la prenotazione non esiste ancora, quindi nessuno può restituire
 * come si chiama la persona appena scelta. Chi già ce l'ha — la scheda vera,
 * che rilegge dal server — il secondo argomento lo ignora.
 */
export function AggiungiGiocatore({
  aggiungi,
  giaInCampo,
  altrove,
}: {
  aggiungi(
    g: { memberId: string | null; guestName: string | null; amountDueCents: number | null },
    nome: string,
  ): void;
  /**
   * Gli identificativi di chi è **già** in campo (`sociGiaInCampo`). Serve a non
   * proporre una scelta che ha una sola risposta, e che è «no».
   */
  giaInCampo: ReadonlySet<string>;
  /**
   * 🎾 21/10/2026 (mockup «Socio alla stessa ora», A): dove gioca già quel socio a quest'ora, o `null`
   * (⇒ `doveGiocaGia`). Nell'elenco è una nota; al tocco, una domanda prima di metterlo in campo.
   */
  altrove?: (memberId: string) => DoveGiocaGia | null;
}): React.JSX.Element {
  const [come, setCome] = useState<'socio' | 'ospite'>('socio');
  const [testo, setTesto] = useState('');
  const [trovati, setTrovati] = useState<PersonaTrovata[]>([]);
  const [daConfermare, setDaConfermare] = useState<{ socio: PersonaTrovata; dove: DoveGiocaGia } | null>(
    null,
  );
  const metti = (s: PersonaTrovata): void => {
    aggiungi({ memberId: s.id, guestName: null, amountDueCents: null }, s.displayName);
    setTesto('');
    setTrovati([]);
    setDaConfermare(null);
  };

  // 250 ms di attesa prima di chiedere: si cerca sul SERVER mentre si digita,
  // mai filtrando in memoria un elenco di 2.800 soci (§ 03.4).
  useEffect(() => {
    if (come !== 'socio' || testo.trim().length < 2) {
      setTrovati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      void chiama<{ items: PersonaTrovata[] }>('GET', `/members/lookup?q=${encodeURIComponent(testo.trim())}`)
        .then((r) => {
          if (!annullato) setTrovati(r.items);
        })
        .catch(() => {
          if (!annullato) setTrovati([]);
        });
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo, come]);

  return (
    <div className="aggiungi-giocatore">
      <div className="scelta">
        <label>
          <input type="radio" checked={come === 'socio'} onChange={() => setCome('socio')} /> Socio
        </label>
        <label>
          <input type="radio" checked={come === 'ospite'} onChange={() => setCome('ospite')} /> Ospite
        </label>
      </div>

      {come === 'socio' ? (
        <>
          <label className="campo">
            <span>Cerca il socio</span>
            <input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="cognome o nome" />
          </label>
          {trovati.length > 0 && (
            <ul className="trovati">
              {trovati.map((s) => {
                const dentro = giaInCampo.has(s.id);
                const dove = dentro ? null : (altrove?.(s.id) ?? null);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="piatto"
                      disabled={dentro}
                      onClick={() => (dove ? setDaConfermare({ socio: s, dove }) : metti(s))}
                    >
                      {s.displayName}
                    </button>
                    {/*
                      ⚖️ Si MOSTRA invece di sparire, ed è il punto della cura.
                      Toglierlo dall'elenco risponderebbe «quel socio non
                      esiste» a chi lo sta cercando — e la riga qui sotto
                      («si può metterlo come ospite») lo direbbe per scritto,
                      spingendo a rimettere in campo la stessa persona come
                      ospite: un secondo posto, e il giorno dell'incasso un
                      secondo addebito.
                    */}
                    {dentro && <span className="tenue piccolo">già in campo</span>}
                    {dove && (
                      <span className="piccolo avvertenza">
                        alle {dove.time} gioca già sul {dove.campo}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {/* 🎾 21/10/2026 (mockup «Socio alla stessa ora», A): un avviso, non un blocco. */}
          {daConfermare && (
            <div className="domanda-riservata" role="alertdialog">
              <p>
                <strong>
                  {daConfermare.socio.displayName} gioca già alle {daConfermare.dove.time}
                </strong>
                <br />È in campo sul {daConfermare.dove.campo}, dalle {daConfermare.dove.time} alle{' '}
                {daConfermare.dove.fine}. Se lo metti anche qui, risulta in due partite alla stessa ora.
              </p>
              <div className="azioni">
                <button type="button" onClick={() => metti(daConfermare.socio)}>
                  Mettilo lo stesso
                </button>
                <button type="button" className="bordato" onClick={() => setDaConfermare(null)}>
                  Annulla
                </button>
              </div>
            </div>
          )}
          {testo.trim().length >= 2 && trovati.length === 0 && (
            <p className="tenue piccolo">Nessun socio con questo nome. Si può metterlo come ospite.</p>
          )}
        </>
      ) : (
        <form
          className="campo orizzontale"
          onSubmit={(e) => {
            e.preventDefault();
            const nome = testo.trim();
            if (nome === '') return;
            aggiungi({ memberId: null, guestName: nome, amountDueCents: null }, nome);
            setTesto('');
          }}
        >
          <label className="campo">
            <span>Nome dell’ospite</span>
            <input value={testo} onChange={(e) => setTesto(e.target.value)} />
          </label>
          <button type="submit">Aggiungi</button>
        </form>
      )}
    </div>
  );
}

/**
 * 🔁⭐⭐ **LA PERIODICITÀ** — 17/09/2026, richiesta sua: *«sempre nella scheda
 * prenotazione, è la periodicità. Quindi la possibilità di rendere una
 * prenotazione periodica»*.
 *
 * ⚖️ **Una data di fine, non un numero di volte**, ed è la scelta che rende la
 * cosa usabile al banco: chi prenota un corso sa *«fino a fine maggio»*, non sa
 * *«diciannove»*. Contarle a mano per scriverne il numero è un conto che si può
 * sbagliare — e che il computer fa da sé, infatti lo scrive qui sotto.
 *
 * ⭐ **Il conto si vede MENTRE si sceglie**: «nascono 12 prenotazioni, di
 * giovedì, l'ultima il 3 dicembre». È l'unica cosa che trasforma una data in
 * una decisione — senza, si preme «Crea» e si scopre dopo quante ne sono nate.
 */
export interface ValoriRipetizione {
  attiva: boolean;
  /**
   * 🔄 28/09/2026 (lista del 28/09, voce 5, 1A): come Matchpoint, due programmazioni — giornaliera o
   * settimanale — col loro «ogni N». Il mensile non si sceglie più: le serie mensili di prima
   * continuano a funzionare, e nella scheda la loro regola si legge e basta.
   */
  famiglia: 'giorni' | 'settimane';
  /** «Ogni N giorno/i» o «ogni N settimana/e». */
  passo: number;
  /** Solo per le settimane. Vuoto = il giorno da cui si parte. */
  giorni: number[];
  fino: string;
}

/** La regola che nasce dai valori della sezione. */
export function regolaDa(v: ValoriRipetizione): RegolaRipetizione & { giorni: number[]; passo: number } {
  return v.famiglia === 'giorni'
    ? { cadenza: 'giornaliera', passo: v.passo, giorni: [] }
    : { cadenza: 'settimanale', passo: v.passo, giorni: v.giorni };
}

/**
 * I valori della sezione da una serie che c'è già, o `null` se la sua regola qui non si sceglie
 * (il mensile, 1A): allora si legge, e si cambia solo la fine.
 */
export function valoriDa(serie: {
  cadenza: Cadenza;
  giorni: number[] | null;
  passo: number | null;
  fino: string;
}): ValoriRipetizione | null {
  const famiglia = CADENZA[serie.cadenza].famiglia;
  if (famiglia === 'mese') return null;
  return {
    attiva: true,
    famiglia,
    passo: passoDi(serie),
    giorni: famiglia === 'settimane' ? [...(serie.giorni ?? [])] : [],
    fino: serie.fino,
  };
}

/**
 * ⭐ Da dove parte la casella della fine: **dodici volte**, che è un trimestre
 * di corso.
 *
 * 🚨 **E per le cadenze a settimane sono dodici SETTIMANE, non dodici volte**,
 * ed è la differenza fra un default che aiuta e uno che tradisce: spuntando
 * mercoledì e venerdì su un corso del lunedì, «dodici volte» tirerebbe la fine
 * indietro a un mese — cioè aggiungere un giorno **accorcerebbe** il corso.
 * ⇒ La fine si conta sempre sul giorno di partenza: gli altri riempiono le
 * stesse settimane, non ne tolgono.
 */
export function finePredefinita(dal: string, regola: RegolaRipetizione): string {
  const soloIlPrimo: RegolaRipetizione =
    CADENZA[regola.cadenza].famiglia === 'settimane' ? { ...regola, giorni: [] } : regola;
  return giorniDellaSerie(dal, soloIlPrimo, '2999-12-31').slice(0, 12).at(-1)!;
}

/**
 * 🔁⭐⭐ **LA SEZIONE «🔁 PRENOTAZIONE RICORRENTE»** — 28/09/2026, lista del 28/09 voce 5 (mockup
 * approvato, 1A 2A 3A). 🗣️ *«una sottosezione dentro la scheda di prenotazione che si attiva
 * flaggando una casella e si apre la sezione della prenotazione ricorrente, sia per prenotarla sia
 * per modificarla»*, come lo screenshot di Matchpoint:
 *   · Data inizio e Data fine;
 *   · Ora inizio e Ora fine (2A): sono quelle della prenotazione, e cambiarne una qui la cambia anche
 *     là — la fine sposta la durata;
 *   · «Programmazione giornaliera, ogni N giorno/i» o «Programmazione settimanale, ogni N
 *     settimana/e» coi giorni da spuntare.
 * Sotto, `sotto`: chi la usa ci mette la riga che dice cosa succede (quante nascono, o cosa cambia
 * salvando). La stessa sezione, uguale, nella prenotazione nuova e nella scheda.
 */
export function SezioneRicorrente({
  dal,
  cambiaDal,
  ora,
  cambiaOra,
  durata,
  cambiaDurata,
  v,
  cambia,
  regolaFissa,
  sotto,
}: {
  dal: string;
  /** Assente = la data inizio non si cambia (una serie che c'è già: la prima volta c'è già stata). */
  cambiaDal?: (g: string) => void;
  ora: string;
  cambiaOra(o: string): void;
  durata: number;
  cambiaDurata(minuti: number): void;
  v: ValoriRipetizione;
  cambia(p: Partial<ValoriRipetizione>): void;
  /** Una regola che qui non si sceglie (il mensile di prima): si legge. */
  regolaFissa?: string;
  sotto?: React.ReactNode;
}): React.JSX.Element {
  const suoGiorno = indiceDelGiorno(dal);
  const scelti = giorniScelti({ cadenza: 'settimanale', giorni: v.giorni }, dal);
  const fine = oraPiuMinuti(ora, durata);
  const tetto = PASSO_MASSIMO[v.famiglia];

  return (
    <>
      <label className="campo orizzontale casella-ricorrente">
        <input type="checkbox" checked={v.attiva} onChange={(e) => cambia({ attiva: e.target.checked })} />
        <span>🔁 Prenotazione ricorrente</span>
      </label>

      {v.attiva && (
        <div className="sezione-ricorrente">
          <div className="due-in-fila">
            <CampoData
              etichetta="Data inizio"
              value={dal}
              disabled={!cambiaDal}
              onChange={(g) => g && cambiaDal?.(g)}
            />
            {/* ⭐ `max` è l'orizzonte vero: un anno. Meglio non poterla battere che battersela e
                vedersela tagliare. */}
            <CampoData
              etichetta="Data fine"
              min={dal}
              max={piuGiorni(dal, ORIZZONTE_GIORNI)}
              value={v.fino}
              onChange={(fino) => fino && cambia({ fino })}
            />
          </div>
          <div className="due-in-fila">
            <label className="campo">
              <span>Ora inizio</span>
              <input
                type="time"
                step={900}
                value={ora}
                onChange={(e) => e.target.value && cambiaOra(e.target.value)}
              />
            </label>
            <label className="campo">
              <span>Ora fine</span>
              <input
                type="time"
                step={900}
                value={fine}
                onChange={(e) => {
                  // La fine sposta la durata; una fine prima dell'inizio non vuol dire niente.
                  const minuti = e.target.value ? minutiDaOra(e.target.value) - minutiDaOra(ora) : 0;
                  if (minuti > 0) cambiaDurata(minuti);
                }}
              />
            </label>
          </div>

          {regolaFissa ? (
            <p className="tenue piccolo">
              Si ripete <strong>{regolaFissa}</strong>: questa regola si legge e basta. Qui puoi cambiare la
              fine, le ore, e annullare le date che vuoi.
            </p>
          ) : (
            <>
              <div className={v.famiglia === 'giorni' ? 'programmazione scelta' : 'programmazione'}>
                <label className="campo orizzontale">
                  <input
                    type="radio"
                    name="programmazione"
                    checked={v.famiglia === 'giorni'}
                    onChange={() => cambia({ famiglia: 'giorni', passo: 1, giorni: [] })}
                  />
                  <span>Programmazione giornaliera</span>
                </label>
                <label className="ogni">
                  ogni
                  <input
                    type="number"
                    min={1}
                    max={PASSO_MASSIMO.giorni}
                    value={v.famiglia === 'giorni' ? v.passo : 1}
                    disabled={v.famiglia !== 'giorni'}
                    onChange={(e) => cambia({ passo: tra(Number(e.target.value), 1, PASSO_MASSIMO.giorni) })}
                  />
                  giorno/i
                </label>
              </div>
              <div className={v.famiglia === 'settimane' ? 'programmazione scelta' : 'programmazione'}>
                <label className="campo orizzontale">
                  <input
                    type="radio"
                    name="programmazione"
                    checked={v.famiglia === 'settimane'}
                    onChange={() => cambia({ famiglia: 'settimane', passo: 1 })}
                  />
                  <span>Programmazione settimanale</span>
                </label>
                <label className="ogni">
                  ogni
                  <input
                    type="number"
                    min={1}
                    max={PASSO_MASSIMO.settimane}
                    value={v.famiglia === 'settimane' ? v.passo : 1}
                    disabled={v.famiglia !== 'settimane'}
                    onChange={(e) =>
                      cambia({ passo: tra(Number(e.target.value), 1, PASSO_MASSIMO.settimane) })
                    }
                  />
                  settimana/e
                </label>
                {v.famiglia === 'settimane' && (
                  <>
                    {/*
                      ⭐ Il giorno da cui si parte è acceso e non si spegne: è **la prenotazione che si
                      sta facendo**, non una scelta (⇒ `giorniDellaSerie`, che lo rimette dentro lo
                      stesso). La riga sotto dice perché: su un telefono il suggerimento non si vede.
                    */}
                    <div className="giorni-settimana">
                      {SIGLE_SETTIMANA.map((sigla, i) => {
                        const suo = i === suoGiorno;
                        const acceso = scelti.includes(i);
                        return (
                          <button
                            key={sigla}
                            type="button"
                            className={acceso ? 'giorno acceso' : 'giorno'}
                            disabled={suo}
                            aria-pressed={acceso}
                            title={suo ? 'È il giorno da cui parte: c’è sempre' : GIORNI_SETTIMANA[i]}
                            onClick={() =>
                              cambia({
                                giorni: acceso
                                  ? scelti.filter((g) => g !== i)
                                  : [...scelti, i].sort((a, b) => a - b),
                              })
                            }
                          >
                            {sigla}
                          </button>
                        );
                      })}
                    </div>
                    <span className="tenue piccolo">
                      Il {GIORNI_SETTIMANA[suoGiorno]} c’è sempre: è il giorno da cui parte.
                    </span>
                  </>
                )}
              </div>
              {v.passo > tetto && <p className="motivo">Al massimo ogni {tetto}.</p>}
            </>
          )}
          {sotto}
        </div>
      )}
    </>
  );
}

const tra = (n: number, min: number, max: number): number =>
  Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : min;

/**
 * La riga della prenotazione nuova: quante nascono, fino a quando, e i due tetti quando mordono.
 *
 * ⚠️ I due tetti si dicono quando MORDONO, non sempre — e **dicendo QUALE**: una serie tagliata in
 * silenzio farebbe credere al circolo di essere prenotato fino a giugno quando lo è fino a
 * gennaio, e una tagliata dicendo il motivo sbagliato manderebbe a cercare la data invece del
 * numero.
 */
export function QuanteNascono({
  dal,
  v,
  ora,
  durata,
}: {
  dal: string;
  v: ValoriRipetizione;
  ora: string;
  durata: number;
}): React.JSX.Element {
  const regola = regolaDa(v);
  const giorni = giorniDellaSerie(dal, regola, v.fino);
  const ultimo = giorni.at(-1)!;
  const oltreLOrizzonte = v.fino > piuGiorni(dal, ORIZZONTE_GIORNI);
  const troppeVolte = giorni.length >= VOLTE_MASSIME && ultimo < v.fino && !oltreLOrizzonte;
  return (
    <p className="riassunto-ricorrente">
      {giorni.length === 1 ? (
        <>
          Così ne nasce <strong>una sola</strong>: la fine è prima della volta dopo.
        </>
      ) : (
        <>
          Nascono <strong>{giorni.length} prenotazioni</strong>, {etichettaCadenza(regola)},{' '}
          {comeSiRipete(dal, regola)}, dalle {ora} alle {oraPiuMinuti(ora, durata)}, dal{' '}
          {giornoSenzaAnno(dal)} al {giornoSenzaAnno(ultimo)}. I giorni già occupati si saltano, e ti si dice
          quali.
        </>
      )}
      {oltreLOrizzonte && <> ⚠️ Oltre l’anno non si va: la serie si ferma al {giornoSenzaAnno(ultimo)}.</>}
      {troppeVolte && (
        <>
          {' '}
          ⚠️ Non più di {VOLTE_MASSIME} volte per volta: la serie si ferma al {giornoSenzaAnno(ultimo)}.
        </>
      )}
    </p>
  );
}

/**
 * 🎾 **Il maestro scelto è già su un altro campo, a quell'ora?** (voce 9, Q3,
 * 27/09/2026, scelta A). Per la segreteria è un **avviso**, non un blocco: un
 * corso diviso su due campi vicini è una cosa che il circolo può voler fare.
 * Al maestro, invece, quelle ore non si offrono e il gestionale lo ferma.
 * Si guarda sulle prenotazioni del giorno già in memoria (quelle della griglia).
 */
export function AvvisoMaestroAltrove(p: {
  prenotazioni: readonly Prenotazione[];
  maestroId: string;
  maestri: readonly Maestro[];
  campi: ReadonlyArray<{ id: string; name: string }>;
  courtId: string;
  date: string;
  time: string;
  durata: number;
  escludiId?: string;
}): React.JSX.Element | null {
  if (p.maestroId === '') return null;
  const da = minutiDaOra(p.time);
  const altrove = maestroGiaAltrove(
    p.prenotazioni,
    p.maestroId,
    p.courtId,
    p.date,
    da,
    da + p.durata,
    p.escludiId,
  );
  if (!altrove) return null;
  const chi = p.maestri.find((m) => m.id === p.maestroId)?.displayName ?? 'Il maestro';
  const dove = p.campi.find((c) => c.id === altrove.courtId)?.name ?? 'un altro campo';
  return (
    <p className="avvertenza">
      ⚠️ <strong>{chi}</strong> alle {altrove.time} ha già{' '}
      {altrove.type === 'corso'
        ? 'un corso'
        : altrove.type === 'allenamento'
          ? 'un allenamento'
          : altrove.type === 'tattica'
            ? 'una partita tattica'
            : 'una lezione'}{' '}
      sul {dove}. Puoi salvare lo stesso.
    </p>
  );
}

/** 🎾 21/10/2026: dove gioca già un socio, a quell'ora (⇒ `doveGiocaGia`). */
export type DoveGiocaGia = { time: string; fine: string; campo: string };

/**
 * 🎾 **Il socio gioca già, a quell'ora, su un altro campo?** (21/10/2026, mockup «Socio alla stessa ora»,
 * scelta A). 🗣️ Due partite di giovedì alle 19:30 con lo stesso socio, messe dalla Segreteria: dal bot non
 * si può. Per la Segreteria è un avviso, non un blocco (si sposta un socio mettendolo nel campo nuovo e
 * togliendolo dal vecchio). Si guarda sulle prenotazioni del giorno già in memoria, come per il maestro.
 */
export function doveGiocaGia(
  p: {
    prenotazioni: readonly Prenotazione[];
    campi: ReadonlyArray<{ id: string; name: string }>;
    date: string;
    time: string;
    durata: number;
    escludiId?: string;
  },
  memberId: string,
): DoveGiocaGia | null {
  const da = minutiDaOra(p.time);
  const gia = socioGiaAltrove(p.prenotazioni, memberId, p.date, da, da + p.durata, p.escludiId);
  if (!gia) return null;
  return {
    time: gia.time,
    fine: oraPiuMinuti(gia.time, gia.durationMinutes),
    campo: p.campi.find((c) => c.id === gia.courtId)?.name ?? 'un altro campo',
  };
}

/** 🎾 Sotto i giocatori: chi di loro è in campo anche altrove a quell'ora (la scheda lo ricorda). */
export function AvvisoSociAltrove(p: {
  prenotazioni: readonly Prenotazione[];
  campi: ReadonlyArray<{ id: string; name: string }>;
  date: string;
  time: string;
  durata: number;
  escludiId?: string;
  giocatori: ReadonlyArray<{ memberId: string | null; nome: string }>;
}): React.JSX.Element | null {
  const righe = p.giocatori.flatMap((g) => {
    if (g.memberId === null) return [];
    const dove = doveGiocaGia(p, g.memberId);
    return dove ? [{ nome: g.nome, dove }] : [];
  });
  if (righe.length === 0) return null;
  return (
    <>
      {righe.map((r) => (
        <p key={r.nome} className="avvertenza">
          ⚠️ <strong>{r.nome}</strong> è anche in campo alle {r.dove.time} sul {r.dove.campo}.
        </p>
      ))}
    </>
  );
}
